import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { Auth2FaActionTypes, T2FaAction } from '../types';
import { Auth2FaService } from '../../services/Auth2FaService';

import { store } from '@/store/configuration';
import { TAppState } from '@/store/reducers/reducers';
import { TAppError } from '@/store/types/base.state';
import { IAuthState } from '@/store/types/auth.types';

import { IAccessToken, IPasskeyParams } from '@/store/models/auth.model';
import { IAuthPasskey } from '@/features/dashboard-users/store/support-user.model';
import { closeModal } from '@/store/actions/core.actions';
import { TCoreAction } from '@/store/types/modals.types';

export const createPasskey = (
  passkeyParams: IPasskeyParams,
  onFinish?: (status: 'success' | 'fail') => void,
): ThunkAction<Promise<void>, TAppState, {}, T2FaAction> => {
  const request = (): T2FaAction => ({
    type: Auth2FaActionTypes.CREATE_PASSKEY,
  });
  const fail = (error: TAppError): T2FaAction => ({
    type: Auth2FaActionTypes.CREATE_PASSKEY_FAIL,
    error,
  });
  const success = (passkey): T2FaAction => ({
    type: Auth2FaActionTypes.CREATE_PASSKEY_SUCCESS,
    passkey,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, T2FaAction>): Promise<void> => {
    dispatch(request());
    try {
      const newPasskey: IAuthPasskey = (await Auth2FaService.createPasskey(passkeyParams)) || ({} as IAuthPasskey);
      dispatch(success(newPasskey));
      onFinish?.('success');
    } catch (error) {
      dispatch(fail(error));
      onFinish?.('fail');
    }
  };
};

export const getPasskey = (
  passkeyId: string,
  onFinish?: () => void,
): ThunkAction<Promise<void>, TAppState, {}, T2FaAction> => {
  const request = (): T2FaAction => ({
    type: Auth2FaActionTypes.GET_PASSKEY,
  });
  const fail = (error: TAppError): T2FaAction => ({
    type: Auth2FaActionTypes.GET_PASSKEY_FAIL,
    error,
  });
  const success = (passkey: IAuthPasskey): T2FaAction => ({
    type: Auth2FaActionTypes.GET_PASSKEY_SUCCESS,
    passkey,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, T2FaAction>): Promise<void> => {
    dispatch(request());
    try {
      const retrivedPasskey: IAuthPasskey = (await Auth2FaService.getPasskey(passkeyId)) || {};

      dispatch(success(retrivedPasskey));
      onFinish?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deletePasskey = (
  passkeyId: string,
  id: string,
  shouldSkipToaster: boolean,
  onFinish?: () => void,
): ThunkAction<Promise<void>, TAppState, {}, T2FaAction | TCoreAction> => {
  const request = (): T2FaAction => ({
    type: Auth2FaActionTypes.DELETE_PASSKEY,
    dashboardUserId: id,
  });
  const fail = (error: TAppError): T2FaAction => ({
    type: Auth2FaActionTypes.DELETE_PASSKEY_FAIL,
    dashboardUserId: id,
    error,
  });
  const success = (passkey: IAuthPasskey): T2FaAction => ({
    type: Auth2FaActionTypes.DELETE_PASSKEY_SUCCESS,
    passkey,
    shouldSkipToaster,
    dashboardUserId: id,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, T2FaAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const retrivedPasskey: IAuthPasskey = (await Auth2FaService.deletePasskey(passkeyId)) || {};

      dispatch(success(retrivedPasskey));
      onFinish?.();
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const authenticate = (
  credentials: string,
  onFinish?: (status: 'success' | 'fail') => void,
): ThunkAction<Promise<void>, TAppState, {}, T2FaAction> => {
  const request = (): T2FaAction => ({
    type: Auth2FaActionTypes.AUTHENTICATE,
  });
  const fail = (error: TAppError): T2FaAction => ({
    type: Auth2FaActionTypes.AUTHENTICATE_FAIL,
    error,
  });
  const success = (token: IAccessToken, credentials: string): T2FaAction => ({
    type: Auth2FaActionTypes.AUTHENTICATE_SUCCESS,
    token,
    credentials,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<IAuthState | TAppState, {}, T2FaAction>): Promise<void> => {
    dispatch(request());

    try {
      const authState: IAuthState = store.getState()?.auth;
      const refreshToken = authState.token?.refresh_token;
      if (!refreshToken) {
        dispatch(fail('No refresh token found!'));
        return;
      }

      const token: IAccessToken = await Auth2FaService.authenticateWithPasskey(refreshToken, credentials);
      dispatch(success(token, credentials));
      onFinish?.('success');
    } catch (error) {
      dispatch(fail(error));
      onFinish?.('fail');
    }
  };
};

export const generateChallengeAction = (
  onFinish?: (challenge: string) => void,
): ThunkAction<Promise<void>, TAppState, {}, T2FaAction> => {
  const request = (): T2FaAction => ({
    type: Auth2FaActionTypes.GENERATE_CHALLENGE,
  });
  const fail = (error: TAppError): T2FaAction => ({
    type: Auth2FaActionTypes.GENERATE_CHALLENGE_FAIL,
    error,
  });
  const success = (): T2FaAction => ({
    type: Auth2FaActionTypes.GENERATE_CHALLENGE_SUCCESS,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, T2FaAction>): Promise<void> => {
    dispatch(request());
    try {
      const { challenge } = await Auth2FaService.generateChallenge();

      dispatch(success());
      onFinish?.(challenge);
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
